import React, { useState, useEffect, useCallback } from "react";
import { X, Search, User, Phone, PackageCheck } from "lucide-react";
import toast from "react-hot-toast";
import { useAuth } from "../../../context/AuthContext";
import { ProductEnquiryService } from "../../../services/api/productEnquiry.service";
import { InverntroyService } from "../../../services/api/inventroy.service";

interface CreateProductEnquiryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  clinicId?: string;
}

const CreateProductEnquiryModal: React.FC<CreateProductEnquiryModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  clinicId,
}) => {
  const { user } = useAuth();
  const effectiveClinicId = clinicId || (user?.role === "clinic" ? user?.id : "") || "";

  const [enquiryItem, setEnquiryItem] = useState("");
  const [patientName, setPatientName] = useState("");
  const [patientMobileNumber, setPatientMobileNumber] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Search suggestions for existing products
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);

  const loadProductOptions = useCallback(async (searchText: string) => {
    if (!searchText || searchText.trim().length < 2) {
      setSearchResults([]);
      setShowDropdown(false);
      return;
    }
    try {
      setLoadingSuggestions(true);
      const response = await InverntroyService.getProductListing({
        page: 1,
        pageSize: 8,
        searchText,
      });

      if (response && Array.isArray(response.data)) {
        const productList = response.data.map((product: any) => ({
          id: product.id,
          name: product.name || "Unnamed Product",
        }));
        setSearchResults(productList);
        setShowDropdown(productList.length > 0);
      } else {
        setSearchResults([]);
        setShowDropdown(false);
      }
    } catch (error) {
      console.error("Error searching products:", error);
    } finally {
      setLoadingSuggestions(false);
    }
  }, []);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      loadProductOptions(enquiryItem);
    }, 350);

    return () => clearTimeout(delayDebounceFn);
  }, [enquiryItem, loadProductOptions]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!effectiveClinicId) {
      toast.error("Clinic ID is missing. Please log in as a clinic.");
      return;
    }

    if (!enquiryItem.trim()) {
      toast.error("Please enter a product or item name");
      return;
    }

    try {
      setSubmitting(true);
      const payload = {
        clinic_id: effectiveClinicId,
        enquiry_item: enquiryItem.trim(),
        patient_name: patientName.trim() || undefined,
        patient_mobile_number: patientMobileNumber.trim() || undefined,
      };

      const res = await ProductEnquiryService.createEnquiry(payload);
      if (res.success) {
        toast.success(res.message || "Product enquiry submitted successfully");
        setEnquiryItem("");
        setPatientName("");
        setPatientMobileNumber("");
        onSuccess();
        onClose();
      } else {
        toast.error(res.message || "Failed to submit enquiry");
      }
    } catch (error) {
      console.error("Error creating enquiry:", error);
      toast.error("Failed to submit enquiry. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-xs flex items-center justify-center z-50 p-4 transition-all">
      <div className="bg-white rounded-xl w-full max-w-lg shadow-xl border border-gray-100 flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-100 bg-gray-50/50">
          <div className="flex items-center space-x-2">
            <div className="p-2 bg-emerald-50 text-emerald-600 rounded-lg">
              <PackageCheck className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">New Product Enquiry</h2>
              <p className="text-xs text-gray-500">Request stock or inquire about unavailable items</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Enquiry Item Input */}
          <div className="relative space-y-1.5">
            <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider">
              Product / Item Name <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search or enter product name (e.g. Paracetamol 500mg)"
                className="w-full pl-9 pr-4 py-2.5 bg-gray-50/50 border border-gray-200 rounded-lg text-sm text-gray-900 focus:bg-white focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 outline-none transition-all"
                value={enquiryItem}
                onChange={(e) => {
                  setEnquiryItem(e.target.value);
                }}
                required
              />
            </div>

            {/* Dropdown Suggestions */}
            {showDropdown && (
              <div className="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-lg shadow-lg mt-1 max-h-48 overflow-y-auto z-20 divide-y divide-gray-50">
                {loadingSuggestions ? (
                  <div className="p-3 text-center text-xs text-gray-500">Searching products...</div>
                ) : (
                  searchResults.map((product) => (
                    <div
                      key={product.id}
                      onClick={() => {
                        setEnquiryItem(product.name);
                        setShowDropdown(false);
                      }}
                      className="p-2.5 hover:bg-emerald-50/60 cursor-pointer text-sm text-gray-800 transition-colors flex items-center justify-between"
                    >
                      <span>{product.name}</span>
                      <span className="text-xs text-emerald-600 font-medium">Select</span>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>

          {/* Patient Name (Optional) */}
          <div className="space-y-1.5">
            <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider">
              Patient Name <span className="text-gray-400 font-normal lowercase">(optional)</span>
            </label>
            <div className="relative">
              <User className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Enter patient name (if applicable)"
                className="w-full pl-9 pr-4 py-2.5 bg-gray-50/50 border border-gray-200 rounded-lg text-sm text-gray-900 focus:bg-white focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 outline-none transition-all"
                value={patientName}
                onChange={(e) => setPatientName(e.target.value)}
              />
            </div>
          </div>

          {/* Patient Mobile Number (Optional) */}
          <div className="space-y-1.5">
            <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider">
              Patient Mobile Number <span className="text-gray-400 font-normal lowercase">(optional)</span>
            </label>
            <div className="relative">
              <Phone className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
              <input
                type="tel"
                placeholder="Enter patient mobile (e.g. +919876543210)"
                className="w-full pl-9 pr-4 py-2.5 bg-gray-50/50 border border-gray-200 rounded-lg text-sm text-gray-900 focus:bg-white focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 outline-none transition-all"
                value={patientMobileNumber}
                onChange={(e) => setPatientMobileNumber(e.target.value)}
              />
            </div>
          </div>

          {/* Footer Actions */}
          <div className="pt-4 flex items-center justify-end space-x-3 border-t border-gray-100">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || !enquiryItem.trim()}
              className="px-5 py-2 text-sm font-medium bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors disabled:opacity-50 shadow-xs"
            >
              {submitting ? "Submitting..." : "Submit Enquiry"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateProductEnquiryModal;
